<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationAPI from 'dashboard/api/conversations';
import ChipPopover from './ChipPopover.vue';
import {
  elapsedSeconds,
  formatDurationLabel,
  formatElapsed,
  resolveHandlingStartedAt,
  timerTone,
  timerToneClasses,
} from './helpers/timer';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
  open: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'close']);
const { t } = useI18n();

const now = ref(Date.now());
const stats = ref(null);
const statsLoaded = ref(false);
let tickId = null;

const startedAt = computed(() => resolveHandlingStartedAt(props.conversation));
const seconds = computed(() => elapsedSeconds(startedAt.value, now.value));
const label = computed(() => formatElapsed(seconds.value));
const toneClass = computed(() => timerToneClasses(timerTone(seconds.value)));

const startTick = () => {
  if (tickId) return;
  tickId = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
};

const stopTick = () => {
  if (!tickId) return;
  window.clearInterval(tickId);
  tickId = null;
};

const loadStats = async () => {
  const id = props.conversation?.id;
  if (!id || statsLoaded.value) return;
  try {
    const { data } = await ConversationAPI.getHandlingStats(id);
    stats.value = data || null;
  } catch {
    stats.value = null;
  } finally {
    statsLoaded.value = true;
  }
};

watch(
  () => props.open,
  open => {
    if (open) loadStats();
  }
);

watch(
  () => props.conversation?.id,
  () => {
    stats.value = null;
    statsLoaded.value = false;
  }
);

onMounted(startTick);
onBeforeUnmount(stopTick);

const countLabel = computed(() => {
  const count =
    stats.value?.handling_count ??
    stats.value?.conversations_count ??
    stats.value?.count;
  return Number.isFinite(Number(count)) ? Number(count) : null;
});

const averageLabel = computed(() => {
  const avg =
    stats.value?.average_duration_seconds ??
    stats.value?.average_seconds ??
    stats.value?.avg_seconds;
  return Number.isFinite(Number(avg)) ? formatDurationLabel(avg) : null;
});

const lastLabel = computed(() => {
  const last =
    stats.value?.last_duration_seconds ?? stats.value?.last_seconds;
  if (Number.isFinite(Number(last))) return formatDurationLabel(last);
  const lastAt = stats.value?.last_handling_at || stats.value?.last_at;
  if (!lastAt) return null;
  const date = new Date(lastAt);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});
</script>

<template>
  <ChipPopover v-if="startedAt" :open="open" @close="emit('close')">
    <template #trigger>
      <button
        type="button"
        data-testid="context-chip-timer"
        class="inline-flex h-8 max-w-full min-w-0 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-bold tabular-nums"
        :class="toneClass"
        :aria-expanded="open"
        :aria-label="t('CONVERSATION.HEADER.CONTEXT_CHIPS.TIMER.ARIA')"
        @click.stop="emit('toggle')"
      >
        <span class="i-ri-time-line size-3.5 shrink-0" aria-hidden="true" />
        <span class="truncate">{{ label }}</span>
      </button>
    </template>

    <p class="text-[13px] font-bold text-n-slate-12">
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TIMER.THIS') }}
    </p>
    <p class="mt-1 text-[20px] font-extrabold tabular-nums text-n-slate-12">
      {{ label }}
    </p>
    <ul v-if="countLabel != null || averageLabel || lastLabel" class="mt-3 space-y-1.5 text-[13px] text-n-slate-11">
      <li v-if="countLabel != null">
        {{
          t('CONVERSATION.HEADER.CONTEXT_CHIPS.TIMER.COUNT', {
            n: countLabel,
          })
        }}
      </li>
      <li v-if="averageLabel">
        {{
          t('CONVERSATION.HEADER.CONTEXT_CHIPS.TIMER.AVERAGE', {
            value: averageLabel,
          })
        }}
      </li>
      <li v-if="lastLabel">
        {{
          t('CONVERSATION.HEADER.CONTEXT_CHIPS.TIMER.LAST', {
            value: lastLabel,
          })
        }}
      </li>
    </ul>
    <p class="mt-3 text-[12px] leading-snug text-n-slate-10">
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TIMER.FOOTER') }}
    </p>
  </ChipPopover>
</template>
